import React, { useState, useEffect } from 'react';
import { FaUserGraduate, FaBed, FaSearch, FaUserPlus, FaEdit, FaTrash, FaTimes, FaFilter, FaExclamationTriangle, FaExchangeAlt, FaUserCheck, FaHome, FaCalendarAlt, FaIdCard } from 'react-icons/fa';
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../../css/HostelStudent.css";

function HostelStudent() {
    const [isLoading, setIsLoading] = useState(false);
    const [students, setStudents] = useState([]);
    const [hostels, setHostels] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOption, setFilterOption] = useState('all');
    const [selectedHostel, setSelectedHostel] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Modals
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showStudentDetailsModal, setShowStudentDetailsModal] = useState(false);
    const [currentStudent, setCurrentStudent] = useState(null);

    // Form data
    const [assignFormData, setAssignFormData] = useState({
        studentId: '',
        hostelId: '',
        roomId: '',
        dateAssigned: '',
        duration: '1',
        rentPaid: 'false',
        amount: '',
        modeOfPayment: 'cash',
    });

    // Available rooms for selected hostel
    const [availableRooms, setAvailableRooms] = useState([]);

    useEffect(() => {
        fetchStudents();
        fetchHostels();
        fetchRooms();
    }, []);

    // Fetch students from backend
    const fetchStudents = async () => {
        setIsLoading(true);

        try {
            // Replace with your actual API endpoint
            const token = localStorage.getItem("token");
            const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch students");
            }

            const data = await response.json();
            setStudents(data);
            setFilteredStudents(data);
        } catch (error) {
            console.error("Error fetching students:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch hostels from backend
    const fetchHostels = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch("https://schoolpro-api.uniums.com/api/hostels", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch hostels");
            }

            const data = await response.json();
            setHostels(data);
        } catch (error) {
            console.error("Error fetching hostels:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    // Fetch rooms from backend
    const fetchRooms = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch("https://schoolpro-api.uniums.com/api/hostels/rooms", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch rooms");
            }

            const data = await response.json();
            setRooms(data);
        } catch (error) {
            console.error("Error fetching rooms:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    // Handle search
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        filterStudents(term, filterOption, selectedHostel);
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        const newFilter = e.target.value;
        setFilterOption(newFilter);
        filterStudents(searchTerm, newFilter, selectedHostel);
    };

    // Handle hostel selection change
    const handleHostelChange = (e) => {
        const hostelId = e.target.value;
        setSelectedHostel(hostelId);
        filterStudents(searchTerm, filterOption, hostelId);

        // Update available rooms for the selected hostel
        if (hostelId) {
            const hostelRooms = rooms.filter(room =>
                room.hostelId === hostelId &&
                room.status === 'available' &&
                room.occupants < room.capacity
            );
            setAvailableRooms(hostelRooms);
        } else {
            setAvailableRooms([]);
        }

        // Update assign form data
        setAssignFormData({
            ...assignFormData,
            hostelId: hostelId,
            roomId: '',
        });
    };

    // Filter students
    const filterStudents = (search, filter, hostel) => {
        let results = [...students];

        // Apply search
        if (search) {
            results = results.filter(student =>
                student.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                student.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                student.admissionNumber?.toLowerCase().includes(search.toLowerCase()) ||
                student.email?.toLowerCase().includes(search.toLowerCase())
            );
        }

        // Apply status filter
        if (filter !== 'all') {
            if (filter === 'assigned') {
                results = results.filter(student => student.hostelRoom);
            } else if (filter === 'unassigned') {
                results = results.filter(student => !student.hostelRoom);
            }
        }

        // Apply hostel filter
        if (hostel) {
            results = results.filter(student =>
                student.hostelRoom && student.hostelRoom.hostelId === hostel
            );
        }

        setFilteredStudents(results);
    };

    // Toggle filters visibility
    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Handle input change for assign form
    const handleAssignInputChange = (e) => {
        const { name, value } = e.target;
        setAssignFormData({
            ...assignFormData,
            [name]: value
        });

        // If hostel is changed, update available rooms
        if (name === 'hostelId') {
            const hostelRooms = rooms.filter(room =>
                room.hostelId === value &&
                room.status === 'available' &&
                room.occupants < room.capacity
            );
            setAvailableRooms(hostelRooms);
        }
    };

    // Handle assign room submission
    const handleAssignRoomSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
    
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostel-assignments/assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(assignFormData)
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to assign room");
            }
    
            const updatedAssignment = await response.json();
    
            // Update the students state with the new assignment
            const updatedStudents = students.map(student =>
                student._id === assignFormData.studentId
                    ? {
                          ...student,
                          hostelRoom: {
                              hostelId: assignFormData.hostelId,
                              roomId: assignFormData.roomId,
                              dateAssigned: assignFormData.dateAssigned,
                              duration: assignFormData.duration,
                              rentPaid: assignFormData.rentPaid === 'true',
                              amount: assignFormData.amount,
                              modeOfPayment: assignFormData.modeOfPayment
                          }
                      }
                    : student
            );
    
            setStudents(updatedStudents);
            setFilteredStudents(updatedStudents);
    
            await fetchRooms(); // Refresh rooms data
    
            toast.success("Room assigned successfully!");
            setShowAssignModal(false);
            resetAssignForm();
        } catch (error) {
            console.error("Error assigning room:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle transfer room submission
    const handleTransferRoomSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
    
        try {
            // Replace with your actual API endpoint
            const token = localStorage.getItem("token");
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostel-assignments/transfer`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    studentId: currentStudent._id,
                    ...assignFormData
                })
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to transfer room");
            }
    
            // Get the updated student data directly from the response
            const updatedStudentData = await response.json();
            
            // Update the student in the students array
            const updatedStudents = students.map(student => 
                student._id === currentStudent._id ? 
                {...student, hostelRoom: updatedStudentData.hostelRoom} : 
                student
            );
    
            setStudents(updatedStudents);
            setFilteredStudents(prevFiltered => 
                prevFiltered.map(student => 
                    student._id === currentStudent._id ? 
                    {...student, hostelRoom: updatedStudentData.hostelRoom} : 
                    student
                )
            );
    
            await fetchRooms(); // Refresh rooms data
    
            toast.success("Room transferred successfully!");
            setShowTransferModal(false);
            resetAssignForm();
        } catch (error) {
            console.error("Error transferring room:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle remove from room
    const handleRemoveFromRoom = async () => {
        setIsLoading(true);
    
        try {
            // Replace with your actual API endpoint
            const token = localStorage.getItem("token");
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostel-assignments/remove/${currentStudent._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to remove student from room");
            }
    
            // Update the student in the students array to remove hostelRoom
            const updatedStudents = students.map(student => 
                student._id === currentStudent._id ? 
                {...student, hostelRoom: null} : 
                student
            );
    
            setStudents(updatedStudents);
            setFilteredStudents(prevFiltered => 
                prevFiltered.map(student => 
                    student._id === currentStudent._id ? 
                    {...student, hostelRoom: null} : 
                    student
                )
            );
    
            await fetchRooms(); // Refresh rooms data
    
            toast.success("Student removed from room successfully!");
            setShowRemoveModal(false);
        } catch (error) {
            console.error("Error removing student:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Reset assign form
    const resetAssignForm = () => {
        setAssignFormData({
            studentId: '',
            hostelId: '',
            roomId: '',
            dateAssigned: '',
            duration: '1',
            rentPaid: 'false',
            amount: '',
            modeOfPayment: 'cash',
        });
        setAvailableRooms([]);
    };

    // Open assign modal for a student
    const handleAssignClick = (student) => {
        setCurrentStudent(student);
        setAssignFormData({
            ...assignFormData,
            studentId: student._id,
            dateAssigned: new Date().toISOString().split('T')[0],
        });
        setShowAssignModal(true);
    };

    // Open transfer modal for a student
    const handleTransferClick = (student) => {
        setCurrentStudent(student);

        // Get current hostel and room details
        const currentHostelId = student.hostelRoom?.hostelId;

        setAssignFormData({
            ...assignFormData,
            studentId: student._id,
            hostelId: currentHostelId || '',
            dateAssigned: new Date().toISOString().split('T')[0],
        });

        // Update available rooms for the selected hostel
        if (currentHostelId) {
            const hostelRooms = rooms.filter(room =>
                room.hostelId === currentHostelId &&
                room.status === 'available' &&
                room.occupants < room.capacity
            );
            setAvailableRooms(hostelRooms);
        }

        setShowTransferModal(true);
    };

    // Open remove modal for a student
    const handleRemoveClick = (student) => {
        setCurrentStudent(student);
        setShowRemoveModal(true);
    };

    // Open student details modal
    const handleStudentDetailsClick = (student) => {
        setCurrentStudent(student);
        setShowStudentDetailsModal(true);
    };

    // Get hostel name by ID
    const getHostelNameById = (hostelId) => {
        const hostel = hostels.find(h => h._id === hostelId);
        return hostel ? hostel.name : 'Unknown Hostel';
    };

    // Get room number by ID
    const getRoomNumberById = (roomId) => {
        const room = rooms.find(r => r._id === roomId);
        return room ? room.roomNumber : 'Unknown Room';
    };

    // Get hostel type by ID
    const getHostelTypeById = (hostelId) => {
        const hostel = hostels.find(h => h._id === hostelId);
        return hostel ? hostel.type : '';
    };

    // Format date to readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="hostelstudent-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="hostelstudent-header">
                <div className="hostelstudent-title-container">
                    <FaUserGraduate className="hostelstudent-icon" />
                    <h1 className="hostelstudent-title">Hostel Student Assignment</h1>
                </div>

                <div className="hostelstudent-controls">
                    <div className="hostelstudent-search-container">
                        <FaSearch className="hostelstudent-search-icon" />
                        <input
                            type="text"
                            className="hostelstudent-search-input"
                            placeholder="Search by name, admission number..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <button className="hostelstudent-filter-toggle-btn" onClick={toggleFilters}>
                        <FaFilter /> Filters
                    </button>

                    <button
                        className="hostelstudent-assign-btn"
                        onClick={() => setShowAssignModal(true)}
                    >
                        <FaUserPlus /> Assign New Student
                    </button>
                </div>

                {showFilters && (
                    <div className="hostelstudent-filter-options">
                        <div className="hostelstudent-filter-group">
                            <label>Status:</label>
                            <select
                                value={filterOption}
                                onChange={handleFilterChange}
                                className="hostelstudent-filter-select"
                            >
                                <option value="all">All Students</option>
                                <option value="assigned">Assigned to Hostel</option>
                                <option value="unassigned">Not Assigned</option>
                            </select>
                        </div>

                        <div className="hostelstudent-filter-group">
                            <label>Hostel:</label>
                            <select
                                value={selectedHostel}
                                onChange={handleHostelChange}
                                className="hostelstudent-filter-select"
                            >
                                <option value="">All Hostels</option>
                                {hostels.map(hostel => (
                                    <option key={hostel._id} value={hostel._id}>{hostel.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="hostelstudent-stats">
                    <div className="hostelstudent-stat-box">
                        <span className="hostelstudent-stat-number">{students.length}</span>
                        <span className="hostelstudent-stat-label">Total Students</span>
                    </div>
                    <div className="hostelstudent-stat-box assigned">
                        <span className="hostelstudent-stat-number">
                            {students.filter(student => student.hostelRoom).length}
                        </span>
                        <span className="hostelstudent-stat-label">Assigned</span>
                    </div>
                    <div className="hostelstudent-stat-box unassigned">
                        <span className="hostelstudent-stat-number">
                            {students.filter(student => !student.hostelRoom).length}
                        </span>
                        <span className="hostelstudent-stat-label">Unassigned</span>
                    </div>
                </div>
            </div>

            {/* Students List */}
            {filteredStudents.length === 0 && !isLoading ? (
                <div className="hostelstudent-empty">
                    <div className="hostelstudent-empty-icon">
                        <FaUserGraduate size={48} color="#a0aec0" />
                    </div>
                    <p className="hostelstudent-empty-text">No students found matching your criteria.</p>
                    {searchTerm && (
                        <button className="hostelstudent-reset-search" onClick={() => setSearchTerm('')}>
                            Clear Search
                        </button>
                    )}
                </div>
            ) : (
                <div className="hostelstudent-table-container">
                    <table className="hostelstudent-table">
                        <thead>
                            <tr>
                                <th>Student Roll No</th>
                                <th>Name</th>
                                <th>Admission #</th>
                                <th>Department/Class</th>
                                <th>Hostel</th>
                                <th>Room</th>
                                <th>Assigned Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStudents.map((student, index) => (
                                <tr key={index} className={student.hostelRoom ? "hostelstudent-row-assigned" : "hostelstudent-row-unassigned"}>
                                    <td>{student.rollNumber || 'N/A'}</td>
                                    <td>
                                        <div className="hostelstudent-name-cell" onClick={() => handleStudentDetailsClick(student)}>
                                            {student.firstName} {student.lastName}
                                        </div>
                                    </td>
                                    <td> {student.admissionDate ? new Date(student.admissionDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                                    <td>{student.grade || 'N/A'}</td>
                                    <td>
                                        {student.hostelRoom ? (
                                            <div className={`hostelstudent-hostel-badge ${getHostelTypeById(student.hostelRoom.hostelId)}`}>
                                                {getHostelNameById(student.hostelRoom.hostelId)}
                                            </div>
                                        ) : (
                                            <span className="hostelstudent-not-assigned">Not Assigned</span>
                                        )}
                                    </td>
                                    <td>
                                        {student.hostelRoom ? (
                                            <div className="hostelstudent-room-badge">
                                                {getRoomNumberById(student.hostelRoom.roomId)}
                                            </div>
                                        ) : (
                                            <span className="hostelstudent-not-assigned">N/A</span>
                                        )}
                                    </td>
                                    <td>
                                        {student.hostelRoom ? (
                                            formatDate(student.hostelRoom.dateAssigned)
                                        ) : (
                                            <span className="hostelstudent-not-assigned">N/A</span>
                                        )}
                                    </td>
                                    <td>
                                        {student.hostelRoom ? (
                                            <span className="hostelstudent-status-badge assigned">
                                                <FaUserCheck className="hostelstudent-status-icon" /> Assigned
                                            </span>
                                        ) : (
                                            <span className="hostelstudent-status-badge unassigned">
                                                <FaTimes className="hostelstudent-status-icon" /> Unassigned
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="hostelstudent-actions">
                                            {student.hostelRoom ? (
                                                <>
                                                    <button
                                                        className="hostelstudent-action-btn hostelstudent-transfer-btn"
                                                        title="Transfer"
                                                        onClick={() => handleTransferClick(student)}
                                                    >
                                                        <FaExchangeAlt />
                                                    </button>
                                                    <button
                                                        className="hostelstudent-action-btn hostelstudent-remove-btn"
                                                        title="Remove"
                                                        onClick={() => handleRemoveClick(student)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    className="hostelstudent-action-btn hostelstudent-assign-btn1"
                                                    title="Assign"
                                                    onClick={() => handleAssignClick(student)}
                                                >
                                                    <FaEdit className='edit_svg'/>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Assign Room Modal */}
            {showAssignModal && (
                <div className="hostelstudent-modal-overlay">
                    <div className="hostelstudent-modal">
                        <div className="hostelstudent-modal-header">
                            <h3>
                                {currentStudent ?
                                    `Assign Room to ${currentStudent.firstName} ${currentStudent.lastName}` :
                                    'Assign Room to Student'
                                }
                            </h3>
                            <button className="hostelstudent-modal-close" onClick={() => setShowAssignModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAssignRoomSubmit} className="hostelstudent-form">
                            {!currentStudent && (
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="studentId">Student *</label>
                                    <select
                                        id="studentId"
                                        name="studentId"
                                        value={assignFormData.studentId}
                                        onChange={handleAssignInputChange}
                                        required
                                    >
                                        <option value="">Select Student</option>
                                        {students
                                            .filter(student => !student.hostelRoom)
                                            .map(student => (
                                                <option key={student._id} value={student._id}>
                                                    {student.firstName} {student.lastName} ({student.admissionNumber})
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                            )}

                            <div className="hostelstudent-form-row">
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="hostelId">Hostel *</label>
                                    <select
                                        id="hostelId"
                                        name="hostelId"
                                        value={assignFormData.hostelId}
                                        onChange={handleAssignInputChange}
                                        required
                                    >
                                        <option value="">Select Hostel</option>
                                        {hostels.map(hostel => (
                                            <option key={hostel._id} value={hostel._id}>{hostel.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="roomId">Room *</label>
                                    <select
                                        id="roomId"
                                        name="roomId"
                                        value={assignFormData.roomId}
                                        onChange={handleAssignInputChange}
                                        required
                                        disabled={!assignFormData.hostelId}
                                    >
                                        <option value="">Select Room</option>
                                        {availableRooms.map(room => (
                                            <option key={room._id} value={room._id}>
                                                Room {room.roomNumber} - {room.occupants}/{room.capacity} occupied
                                            </option>
                                        ))}
                                        {assignFormData.hostelId && availableRooms.length === 0 && (
                                            <option value="" disabled>No available rooms in this hostel</option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            <div className="hostelstudent-form-row">
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="dateAssigned">Date Assigned *</label>
                                    <input
                                        type="date"
                                        id="dateAssigned"
                                        name="dateAssigned"
                                        value={assignFormData.dateAssigned}
                                        onChange={handleAssignInputChange}
                                        required
                                    />
                                </div>
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="duration">Duration (months) *</label>
                                    <input
                                        type="number"
                                        id="duration"
                                        name="duration"
                                        value={assignFormData.duration}
                                        onChange={handleAssignInputChange}
                                        required
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div className="hostelstudent-form-row">
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="rentPaid">Rent Paid *</label>
                                    <select
                                        id="rentPaid"
                                        name="rentPaid"
                                        value={assignFormData.rentPaid}
                                        onChange={handleAssignInputChange}
                                        required
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>
                                </div>
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="amount">Amount (₹)</label>
                                    <input
                                        type="number"
                                        id="amount"
                                        name="amount"
                                        value={assignFormData.amount}
                                        onChange={handleAssignInputChange}
                                        placeholder="Enter paid amount"
                                        disabled={assignFormData.rentPaid === 'false'}
                                        required={assignFormData.rentPaid === 'true'}
                                    />
                                </div>
                            </div>

                            {assignFormData.rentPaid === 'true' && (
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="modeOfPayment">Payment Mode *</label>
                                    <select
                                        id="modeOfPayment"
                                        name="modeOfPayment"
                                        value={assignFormData.modeOfPayment}
                                        onChange={handleAssignInputChange}
                                        required
                                    >
                                        <option value="cash">Cash</option>
                                        <option value="online">Online Transfer</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            )}

                            <div className="hostelstudent-form-actions">
                                <button type="button" className="hostelstudent-cancel-btn" onClick={() => setShowAssignModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="hostelstudent-submit-btn">
                                    Assign Room
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Transfer Room Modal */}
            {showTransferModal && (
                <div className="hostelstudent-modal-overlay">
                    <div className="hostelstudent-modal">
                        <div className="hostelstudent-modal-header">
                            <h3>Transfer {currentStudent?.firstName} {currentStudent?.lastName} to New Room</h3>
                            <button className="hostelstudent-modal-close" onClick={() => setShowTransferModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="hostelstudent-current-assignment">
                            <h4>Current Assignment</h4>
                            <div className="hostelstudent-assignment-details">
                                <div className="hostelstudent-detail-item">
                                    <FaHome className="hostelstudent-detail-icon" />
                                    <span>Hostel: {getHostelNameById(currentStudent?.hostelRoom?.hostelId)}</span>
                                </div>
                                <div className="hostelstudent-detail-item">
                                    <FaBed className="hostelstudent-detail-icon" />
                                    <span>Room: {getRoomNumberById(currentStudent?.hostelRoom?.roomId)}</span>
                                </div>
                                <div className="hostelstudent-detail-item">
                                    <FaCalendarAlt className="hostelstudent-detail-icon" />
                                    <span>Assigned Date: {formatDate(currentStudent?.hostelRoom?.dateAssigned)}</span>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleTransferRoomSubmit} className="hostelstudent-form">
                            <div className="hostelstudent-form-row">
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="transfer-hostelId">New Hostel *</label>
                                    <select
                                        id="transfer-hostelId"
                                        name="hostelId"
                                        value={assignFormData.hostelId}
                                        onChange={handleAssignInputChange}
                                        required
                                    >
                                        <option value="">Select Hostel</option>
                                        {hostels.map(hostel => (
                                            <option key={hostel._id} value={hostel._id}>{hostel.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="transfer-roomId">New Room *</label>
                                    <select
                                        id="transfer-roomId"
                                        name="roomId"
                                        value={assignFormData.roomId}
                                        onChange={handleAssignInputChange}
                                        required
                                        disabled={!assignFormData.hostelId}
                                    >
                                        <option value="">Select Room</option>
                                        {availableRooms.map(room => (
                                            <option key={room._id} value={room._id}>
                                                Room {room.roomNumber} - {room.occupants}/{room.capacity} occupied
                                            </option>
                                        ))}
                                        {assignFormData.hostelId && availableRooms.length === 0 && (
                                            <option value="" disabled>No available rooms in this hostel</option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            <div className="hostelstudent-form-row">
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="transfer-dateAssigned">Transfer Date *</label>
                                    <input
                                        type="date"
                                        id="transfer-dateAssigned"
                                        name="dateAssigned"
                                        value={assignFormData.dateAssigned}
                                        onChange={handleAssignInputChange}
                                        required
                                    />
                                </div>
                                <div className="hostelstudent-form-group">
                                    <label htmlFor="transfer-duration">Duration (months) *</label>
                                    <input
                                        type="number"
                                        id="transfer-duration"
                                        name="duration"
                                        value={assignFormData.duration}
                                        onChange={handleAssignInputChange}
                                        required
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div className="hostelstudent-form-group">
                                <label htmlFor="transfer-reason">Reason for Transfer</label>
                                <textarea
                                    id="transfer-reason"
                                    name="reason"
                                    value={assignFormData.reason || ''}
                                    onChange={handleAssignInputChange}
                                    placeholder="Provide reason for transfer (optional)"
                                    rows="3"
                                />
                            </div>

                            <div className="hostelstudent-warning">
                                <FaExclamationTriangle className="hostelstudent-warning-icon" />
                                <p>Student will be removed from their current room assignment and moved to the new room.</p>
                            </div>

                            <div className="hostelstudent-form-actions">
                                <button type="button" className="hostelstudent-cancel-btn" onClick={() => setShowTransferModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="hostelstudent-submit-btn">
                                    Transfer Student
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Remove from Room Modal */}
            {showRemoveModal && (
                <div className="hostelstudent-modal-overlay">
                    <div className="hostelstudent-modal">
                        <div className="hostelstudent-modal-header">
                            <h3>Remove {currentStudent?.firstName} {currentStudent?.lastName} from Room</h3>
                            <button className="hostelstudent-modal-close" onClick={() => setShowRemoveModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="hostelstudent-confirm-content">
                            <div className="hostelstudent-warning">
                                <FaExclamationTriangle className="hostelstudent-warning-icon" />
                                <p>
                                    Are you sure you want to remove this student from their current room assignment?
                                    This action cannot be undone.
                                </p>
                            </div>

                            <div className="hostelstudent-current-assignment">
                                <h4>Current Assignment</h4>
                                <div className="hostelstudent-assignment-details">
                                    <div className="hostelstudent-detail-item">
                                        <FaHome className="hostelstudent-detail-icon" />
                                        <span>Hostel: {getHostelNameById(currentStudent?.hostelRoom?.hostelId)}</span>
                                    </div>
                                    <div className="hostelstudent-detail-item">
                                        <FaBed className="hostelstudent-detail-icon" />
                                        <span>Room: {getRoomNumberById(currentStudent?.hostelRoom?.roomId)}</span>
                                    </div>
                                    <div className="hostelstudent-detail-item">
                                        <FaCalendarAlt className="hostelstudent-detail-icon" />
                                        <span>Assigned Date: {formatDate(currentStudent?.hostelRoom?.dateAssigned)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="hostelstudent-form-actions">
                                <button type="button" className="hostelstudent-cancel-btn" onClick={() => setShowRemoveModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="hostelstudent-delete-btn"
                                    onClick={handleRemoveFromRoom}
                                >
                                    Remove from Room
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Student Details Modal */}
            {showStudentDetailsModal && currentStudent && (
                <div className="hostelstudent-modal-overlay">
                    <div className="hostelstudent-modal hostelstudent-details-modal">
                        <div className="hostelstudent-modal-header">
                            <h3>Student Details</h3>
                            <button className="hostelstudent-modal-close" onClick={() => setShowStudentDetailsModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="hostelstudent-details-content">
                            <div className="hostelstudent-details-profile">
                                <div className="hostelstudent-student-avatar">
                                    {currentStudent.firstName?.charAt(0)}{currentStudent.lastName?.charAt(0)}
                                </div>
                                <div className="hostelstudent-student-info">
                                    <h2>{currentStudent.firstName} {currentStudent.lastName}</h2>
                                    <p className="hostelstudent-student-id">{currentStudent.studentId || currentStudent.admissionNumber}</p>
                                </div>
                            </div>

                            <div className="hostelstudent-details-grid">
                                <div className="hostelstudent-detail-group">
                                    <div className="hostelstudent-detail-label">
                                        <FaIdCard className="hostelstudent-detail-icon" />
                                        <span>Student ID</span>
                                    </div>
                                    <div className="hostelstudent-detail-value">{currentStudent.studentId || 'N/A'}</div>
                                </div>

                                <div className="hostelstudent-detail-group">
                                    <div className="hostelstudent-detail-label">
                                        <FaUserGraduate className="hostelstudent-detail-icon" />
                                        <span>Admission Number</span>
                                    </div>
                                    <div className="hostelstudent-detail-value">{currentStudent.admissionNumber || 'N/A'}</div>
                                </div>

                                <div className="hostelstudent-detail-group">
                                    <div className="hostelstudent-detail-label">
                                        <FaUserGraduate className="hostelstudent-detail-icon" />
                                        <span>Department</span>
                                    </div>
                                    <div className="hostelstudent-detail-value">{currentStudent.department || 'N/A'}</div>
                                </div>

                                <div className="hostelstudent-detail-group">
                                    <div className="hostelstudent-detail-label">
                                        <FaUserGraduate className="hostelstudent-detail-icon" />
                                        <span>Course</span>
                                    </div>
                                    <div className="hostelstudent-detail-value">{currentStudent.course || 'N/A'}</div>
                                </div>

                                <div className="hostelstudent-detail-group">
                                    <div className="hostelstudent-detail-label">
                                        <FaUserGraduate className="hostelstudent-detail-icon" />
                                        <span>Academic Year</span>
                                    </div>
                                    <div className="hostelstudent-detail-value">{currentStudent.academicYear || 'N/A'}</div>
                                </div>

                                <div className="hostelstudent-detail-group">
                                    <div className="hostelstudent-detail-label">
                                        <FaUserGraduate className="hostelstudent-detail-icon" />
                                        <span>Gender</span>
                                    </div>
                                    <div className="hostelstudent-detail-value">{currentStudent.gender || 'N/A'}</div>
                                </div>
                            </div>

                            {currentStudent.hostelRoom ? (
                                <div className="hostelstudent-room-assignment-section">
                                    <h3>Hostel Assignment</h3>
                                    <div className="hostelstudent-assignment-card">
                                        <div className="hostelstudent-assignment-header">
                                            <div className={`hostelstudent-hostel-badge ${getHostelTypeById(currentStudent.hostelRoom.hostelId)}`}>
                                                {getHostelNameById(currentStudent.hostelRoom.hostelId)}
                                            </div>
                                            <div className="hostelstudent-room-badge">
                                                Room {getRoomNumberById(currentStudent.hostelRoom.roomId)}
                                            </div>
                                        </div>
                                        <div className="hostelstudent-assignment-details">
                                            <div className="hostelstudent-detail-item">
                                                <FaCalendarAlt className="hostelstudent-detail-icon" />
                                                <span>Assigned: {formatDate(currentStudent.hostelRoom.dateAssigned)}</span>
                                            </div>
                                            <div className="hostelstudent-detail-item">
                                                <FaCalendarAlt className="hostelstudent-detail-icon" />
                                                <span>Duration: {currentStudent.hostelRoom.duration || 'N/A'} months</span>
                                            </div>
                                            <div className="hostelstudent-detail-item">
                                                <FaCalendarAlt className="hostelstudent-detail-icon" />
                                                <span>Rent Paid: {currentStudent.hostelRoom.rentPaid ? 'Yes' : 'No'}</span>
                                            </div>
                                            {currentStudent.hostelRoom.rentPaid && (
                                                <div className="hostelstudent-detail-item">
                                                    <FaCalendarAlt className="hostelstudent-detail-icon" />
                                                    <span>Amount: ₹{currentStudent.hostelRoom.amount || 'N/A'}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="hostelstudent-assignment-actions">
                                            <button
                                                className="hostelstudent-action-btn hostelstudent-transfer-btn"
                                                onClick={() => {
                                                    setShowStudentDetailsModal(false);
                                                    handleTransferClick(currentStudent);
                                                }}
                                            >
                                                <FaExchangeAlt /> Transfer
                                            </button>
                                            <button
                                                className="hostelstudent-action-btn hostelstudent-remove-btn"
                                                onClick={() => {
                                                    setShowStudentDetailsModal(false);
                                                    handleRemoveClick(currentStudent);
                                                }}
                                            >
                                                <FaTrash /> Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="hostelstudent-no-assignment">
                                    <FaExclamationTriangle className="hostelstudent-warning-icon" />
                                    <p>This student is not currently assigned to any hostel room.</p>
                                    <button
                                        className="hostelstudent-action-btn hostelstudent-assign-btn"
                                        onClick={() => {
                                            setShowStudentDetailsModal(false);
                                            handleAssignClick(currentStudent);
                                        }}
                                    >
                                        <FaUserPlus /> Assign Room
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default HostelStudent;